<template>
  <Modal
    :is-open="isOpen"
    :title="title"
    :subtitle="subtitle"
    :icon="icon"
    :icon-color="iconColor"
    size="sm"
    :show-footer="true"
    :show-cancel-button="true"
    :show-confirm-button="true"
    :cancel-text="cancelText"
    :confirm-text="confirmText"
    :confirm-disabled="confirmDisabled"
    @close="$emit('cancel')"
    @confirm="$emit('confirm')"
  >
    <div class="p-6">
      <div class="text-center">
        <!-- 大图标 -->
        <div 
          class="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
          :class="largeIconClasses"
        >
          <component :is="icon" class="w-8 h-8 text-white" />
        </div>
        
        <!-- 消息内容 -->
        <div class="space-y-2">
          <p class="text-lg font-medium text-gray-900">
            {{ message }}
          </p>
          <p v-if="description" class="text-sm text-gray-600">
            {{ description }}
          </p>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { AlertTriangle, Trash2, CheckCircle, Info, HelpCircle } from 'lucide-vue-next'
import Modal from './Modal.vue'

interface Props {
  isOpen: boolean
  type?: 'warning' | 'danger' | 'success' | 'info' | 'question'
  title?: string
  subtitle?: string
  message: string
  description?: string
  cancelText?: string
  confirmText?: string
  confirmDisabled?: boolean
}

interface Emits {
  (e: 'confirm'): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  type: 'warning',
  title: '确认操作',
  cancelText: '取消',
  confirmText: '确认',
  confirmDisabled: false,
})

const emit = defineEmits<Emits>()

const config = computed(() => {
  const configs = {
    warning: {
      icon: AlertTriangle,
      iconColor: 'yellow' as const,
      title: '警告',
      subtitle: '请确认您的操作'
    },
    danger: {
      icon: Trash2,
      iconColor: 'red' as const,
      title: '危险操作',
      subtitle: '此操作不可撤销'
    },
    success: {
      icon: CheckCircle,
      iconColor: 'green' as const,
      title: '成功',
      subtitle: '操作已完成'
    },
    info: {
      icon: Info,
      iconColor: 'blue' as const,
      title: '信息',
      subtitle: '请查看以下信息'
    },
    question: {
      icon: HelpCircle,
      iconColor: 'purple' as const,
      title: '确认',
      subtitle: '请确认您的选择'
    }
  }
  return configs[props.type]
})

const icon = computed(() => config.value.icon)
const iconColor = computed(() => config.value.iconColor)
const title = computed(() => props.title || config.value.title)
const subtitle = computed(() => props.subtitle || config.value.subtitle)

const largeIconClasses = computed(() => {
  const colors = {
    red: 'bg-gradient-to-br from-red-100 to-red-200 border-2 border-red-300',
    blue: 'bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300',
    green: 'bg-gradient-to-br from-green-100 to-green-200 border-2 border-green-300',
    purple: 'bg-gradient-to-br from-purple-100 to-purple-200 border-2 border-purple-300',
    yellow: 'bg-gradient-to-br from-yellow-100 to-yellow-200 border-2 border-yellow-300',
    gray: 'bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-gray-300'
  }
  return colors[iconColor.value]
})
</script>
